const BALLOON_SIZE_PRESETS = [
  { min: 68, max: 82, weight: 0.28 },
  { min: 84, max: 100, weight: 0.46 },
  { min: 102, max: 118, weight: 0.26 }
];

const LAYOUT_CONFIG = {
  arch: {
    count: 38,
    centerYRatio: 0.62,
    radiusXRatio: 0.34,
    radiusYRatio: 0.38,
    jitterX: 10,
    jitterY: 8,
    rotationInfluence: 0.42,
    rotationJitter: 3,
    imageScale: 1
  },
  wall: {
    columns: 8,
    rows: 9,
    jitterX: 0.3,
    jitterY: 0.3,
    rowOffsetRatio: 0.5,
    rotationJitter: 0.8,
    sizeMinScale: 0.97,
    sizeMaxScale: 1.04,
    gapXRatio: 0.6,
    gapYRatio: 0.52,
    horizontalPaddingRatio: 0.08,
    verticalPaddingRatio: 0.12,
    imageScale: 1.5
  }
};

function hashString(value) {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function createSeededRandom(seedValue) {
  let seed = hashString(seedValue) || 1;

  return () => {
    seed += 0x6d2b79f5;
    let result = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    result ^= result + Math.imul(result ^ (result >>> 7), 61 | result);
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
  };
}

function toDegrees(radians) {
  return radians * (180 / Math.PI);
}

class BalloonBuilderEngine {
  constructor({ resolveAssetUrl }) {
    this.resolveAssetUrl = resolveAssetUrl;
  }

  normalizeLayout(layout) {
    return layout === 'wall' ? 'wall' : 'arch';
  }

  generateScene(state) {
    const seed = [
      this.normalizeLayout(state.layout),
      state.colors.join('|'),
      state.canvasWidth,
      state.canvasHeight,
      state.maxColors
    ].join(':');
    const random = createSeededRandom(seed);
    const items =
      this.normalizeLayout(state.layout) === 'wall' ? this.generateWall(state, random) : this.generateArch(state, random);

    return this.applyDepth(items);
  }

  randomBetween(random, min, max) {
    return min + (max - min) * random();
  }

  pickWeightedSize(random) {
    const threshold = random();
    let cumulativeWeight = 0;

    for (const preset of BALLOON_SIZE_PRESETS) {
      cumulativeWeight += preset.weight;
      if (threshold <= cumulativeWeight) {
        return this.randomBetween(random, preset.min, preset.max);
      }
    }

    const fallbackPreset = BALLOON_SIZE_PRESETS[BALLOON_SIZE_PRESETS.length - 1];
    return this.randomBetween(random, fallbackPreset.min, fallbackPreset.max);
  }

  pickColor(state, index) {
    return state.colors[index % state.colors.length];
  }

  createBalloon(random, options) {
    const size = Math.round(options.sizeOverride || this.pickWeightedSize(random));

    return {
      x: options.x,
      y: options.y,
      size,
      layout: options.layout,
      color: options.color,
      rotation: Math.round(options.rotation * 10) / 10,
      assetUrl: this.resolveAssetUrl(options.color),
      imageScale: options.imageScale || 1,
      shadowY: Math.max(8, Math.round(size * 0.08)),
      shadowBlur: Math.max(12, Math.round(size * 0.18))
    };
  }

  applyDepth(items) {
    return [...items]
      .sort((firstItem, secondItem) => firstItem.y - secondItem.y)
      .map((item, index) => ({
        ...item,
        z: index + 1
      }));
  }

  generateArch(state, random) {
    const balloons = [];
    const config = LAYOUT_CONFIG.arch;
    const centerX = state.canvasWidth / 2;
    const centerY = state.canvasHeight * config.centerYRatio;
    const radiusX = state.canvasWidth * config.radiusXRatio;
    const radiusY = state.canvasHeight * config.radiusYRatio;

    for (let index = 0; index < config.count; index += 1) {
      const progress = index / (config.count - 1);
      const angle = Math.PI - (Math.PI * progress);
      const x =
        centerX +
        (Math.cos(angle) * radiusX) +
        this.randomBetween(random, -config.jitterX, config.jitterX);
      const y =
        centerY -
        (Math.sin(angle) * radiusY) +
        this.randomBetween(random, -config.jitterY, config.jitterY);
      const curveRotation = toDegrees((Math.PI / 2) - angle) * config.rotationInfluence;
      const rotation = curveRotation + this.randomBetween(random, -config.rotationJitter, config.rotationJitter);
      const color = this.pickColor(state, index);

      balloons.push(
        this.createBalloon(random, {
          x,
          y,
          layout: 'arch',
          color,
          rotation,
          imageScale: config.imageScale
        })
      );
    }

    return balloons;
  }

  generateWall(state, random) {
    const balloons = [];
    const config = LAYOUT_CONFIG.wall;
    const packedBaseSize = Math.min(
      state.canvasWidth /
        (((config.columns - 1) * config.gapXRatio) + config.rowOffsetRatio + 1 + config.horizontalPaddingRatio),
      state.canvasHeight /
        (((config.rows - 1) * config.gapYRatio) + 1 + config.verticalPaddingRatio)
    );
    const gapX = packedBaseSize * config.gapXRatio;
    const gapY = packedBaseSize * config.gapYRatio;
    const rowOffset = gapX * config.rowOffsetRatio;
    const totalWidth = ((config.columns - 1) * gapX) + rowOffset + packedBaseSize;
    const totalHeight = ((config.rows - 1) * gapY) + packedBaseSize;
    const startX = ((state.canvasWidth - totalWidth) / 2) + (packedBaseSize / 2);
    const startY = ((state.canvasHeight - totalHeight) / 2) + (packedBaseSize / 2);
    let index = 0;

    for (let row = 0; row < config.rows; row += 1) {
      for (let column = 0; column < config.columns; column += 1) {
        const sizeOverride =
          packedBaseSize * this.randomBetween(random, config.sizeMinScale, config.sizeMaxScale);
        const x =
          startX +
          (column * gapX) +
          (row % 2 === 0 ? rowOffset : 0) +
          this.randomBetween(random, -config.jitterX, config.jitterX);
        const y =
          startY +
          (row * gapY) +
          this.randomBetween(random, -config.jitterY, config.jitterY);
        const rotation = this.randomBetween(random, -config.rotationJitter, config.rotationJitter);
        const color = this.pickColor(state, index);

        balloons.push(
          this.createBalloon(random, {
            x,
            y,
            layout: 'wall',
            color,
            rotation,
            sizeOverride,
            imageScale: config.imageScale
          })
        );
        index += 1;
      }
    }

    return balloons;
  }
}

class BalloonBuilderRenderer {
  constructor(root) {
    this.stage = root.querySelector('#BalloonBuilderStage');
    this.priceEl = root.querySelector('#BalloonBuilderPrice');
    this.currency = root.dataset.currency || '₪';
    this.currencyCode = root.dataset.currencyCode || 'ILS';
    this.locale = root.dataset.locale || document.documentElement.lang || 'he-IL';
  }

  renderBalloon(item) {
    return `
      <div
        class="builder-balloon"
        data-layout="${item.layout}"
        style="
          left:${item.x.toFixed(1)}px;
          top:${item.y.toFixed(1)}px;
          width:${item.size}px;
          height:${item.size}px;
          z-index:${item.z};
          transform:translate(-50%, -50%) rotate(${item.rotation}deg);
          --balloon-color:${item.color};
          --balloon-shadow-y:${item.shadowY}px;
          --balloon-shadow-blur:${item.shadowBlur}px;
          --balloon-image-scale:${item.imageScale};
        "
      >
        ${
          item.assetUrl
            ? `<img class="builder-balloon__image" src="${item.assetUrl}" alt="" loading="lazy" decoding="async">`
            : `<span class="builder-balloon__shape"></span>
               <span class="builder-balloon__shine"></span>
               <span class="builder-balloon__knot"></span>`
        }
      </div>
    `;
  }

  render(items) {
    if (!this.stage) return;
    this.stage.innerHTML = items.map((item) => this.renderBalloon(item)).join('');
  }

  formatPrice(total) {
    if (typeof Intl !== 'undefined') {
      try {
        const amount = new Intl.NumberFormat(this.locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(total);

        return `${this.currency}${amount}`;
      } catch (error) {
        return `${Math.round(total)} ${this.currency}`;
      }
    }

    return `${Math.round(total)} ${this.currency}`;
  }

  updatePrice(total) {
    if (this.priceEl) {
      this.priceEl.textContent = this.formatPrice(total);
    }
  }
}

class BalloonBuilder {
  constructor(root) {
    this.root = root;
    this.layoutButtons = [...root.querySelectorAll('.builder-layout-btn')];
    this.colorButtons = [...root.querySelectorAll('.builder-color-btn')];
    this.quantityButtons = [...root.querySelectorAll('.builder-quantity-btn')];
    this.compositionButtons = [...root.querySelectorAll('.builder-composition-btn')];
    this.defaultColors = this.parseColors(root.dataset.defaultColors);
    this.assetMap = this.parseAssetMap(root.dataset.balloonAssets);
    this.renderer = new BalloonBuilderRenderer(root);
    this.engine = new BalloonBuilderEngine({
      resolveAssetUrl: (color) => this.getAssetUrlForColor(color)
    });

    this.state = {
      layout: this.engine.normalizeLayout(root.dataset.defaultLayout || 'arch'),
      colors: this.defaultColors.length
        ? this.defaultColors
        : [this.colorButtons[0]?.dataset.color || '#d4af37'],
      maxColors: Number(root.dataset.maxColors || 4),
      basePrice: Number(root.dataset.basePrice || 0),
      balloonCount: Number(root.dataset.defaultBalloonCount || 100),
      compositionId: this.compositionButtons[0]?.dataset.compositionId || '',
      compositionLabel: this.compositionButtons[0]?.dataset.compositionLabel || '',
      canvasWidth: Number(root.dataset.canvasWidth || 520),
      canvasHeight: Number(root.dataset.canvasHeight || 640)
    };

    if (!this.renderer.stage) return;

    this.bind();
    this.syncControls();
    this.render();
    this.syncFormProperties();
  }

  parseColors(colorsValue) {
    if (!colorsValue) return [];

    return colorsValue
      .split(',')
      .map((color) => color.trim())
      .filter(Boolean);
  }

  parseAssetMap(assetMapValue) {
    if (!assetMapValue) return {};

    try {
      return JSON.parse(assetMapValue);
    } catch (error) {
      return {};
    }
  }

  bind() {
    this.layoutButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.state.layout = button.dataset.layout;
        this.syncControls();
        this.render();
        this.syncFormProperties();
      });
    });

    this.colorButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const color = button.dataset.color;
        const exists = this.state.colors.includes(color);

        if (exists) {
          this.state.colors = this.state.colors.filter((currentColor) => currentColor !== color);
        } else if (this.state.colors.length < this.state.maxColors) {
          this.state.colors.push(color);
        }

        if (this.state.colors.length === 0) {
          this.state.colors = [this.colorButtons[0]?.dataset.color || '#d4af37'];
        }

        this.syncControls();
        this.render();
        this.syncFormProperties();
      });
    });

    this.quantityButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const nextCount = Number(button.dataset.balloonCount || 100);

        if (!Number.isFinite(nextCount) || nextCount < 100) return;

        this.state.balloonCount = nextCount;
        this.syncControls();
        this.render();
        this.syncFormProperties();
      });
    });

    this.compositionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.state.compositionId = button.dataset.compositionId || '';
        this.state.compositionLabel = button.dataset.compositionLabel || '';
        this.syncControls();
        this.syncFormProperties();
      });
    });
  }

  syncControls() {
    this.layoutButtons.forEach((button) => {
      const isActive = button.dataset.layout === this.state.layout;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    this.colorButtons.forEach((button) => {
      const isActive = this.state.colors.includes(button.dataset.color);
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    this.quantityButtons.forEach((button) => {
      const isActive = Number(button.dataset.balloonCount || 0) === this.state.balloonCount;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    this.compositionButtons.forEach((button) => {
      const isActive = (button.dataset.compositionId || '') === this.state.compositionId;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
  }

  getAssetKeyForColor(color) {
    const button = this.colorButtons.find((item) => item.dataset.color === color);
    return button?.dataset.asset || '';
  }

  getAssetUrlForColor(color) {
    const assetKey = this.getAssetKeyForColor(color);
    return this.assetMap[assetKey] || '';
  }

  render() {
    const items = this.engine.generateScene(this.state);
    this.renderer.render(items);

    const total = this.state.basePrice * (this.state.balloonCount / 100);
    this.renderer.updatePrice(total);
  }

  syncFormProperties() {
    const quantityInput = this.root.querySelector('#BalloonBuilderProductQuantity');
    const layoutInput = this.root.querySelector('#BalloonBuilderLayoutProperty');
    const colorsInput = this.root.querySelector('#BalloonBuilderColorsProperty');
    const countInput = this.root.querySelector('#BalloonBuilderCountProperty');
    const compositionInput = this.root.querySelector('#BalloonBuilderCompositionProperty');
    const compositionLabelInput = this.root.querySelector('#BalloonBuilderCompositionLabelProperty');
    const configInput = this.root.querySelector('#BalloonBuilderConfigProperty');
    const productQuantity = Math.max(1, Math.round(this.state.balloonCount / 100));

    if (quantityInput) quantityInput.value = String(productQuantity);
    if (layoutInput) layoutInput.value = this.state.layout;
    if (colorsInput) colorsInput.value = this.state.colors.join(', ');
    if (countInput) countInput.value = String(this.state.balloonCount);
    if (compositionInput) compositionInput.value = this.state.compositionId;
    if (compositionLabelInput) compositionLabelInput.value = this.state.compositionLabel;
    if (configInput) {
      configInput.value = JSON.stringify({
        layout: this.state.layout,
        colors: this.state.colors,
        balloonCount: this.state.balloonCount,
        compositionId: this.state.compositionId,
        compositionLabel: this.state.compositionLabel
      });
    }
  }
}

function initBalloonBuilders(container = document) {
  const roots = container.querySelectorAll('[data-balloon-builder-root]');

  roots.forEach((root) => {
    if (root.dataset.balloonBuilderInitialized === 'true') return;
    root.dataset.balloonBuilderInitialized = 'true';
    new BalloonBuilder(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initBalloonBuilders();
});

document.addEventListener('shopify:section:load', (event) => {
  initBalloonBuilders(event.target);
});
